import {
  validatePaginatedResponseWithSchema,
  validateWithSchema,
} from "@/helpers/common";
import { formatString } from "@/helpers/formatting";
import {
  accessTokenSchema,
  createdAccessTokenSchema,
} from "@/schemas/accessTokens";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type { IPaginatedResponse } from "@/models/common";
import type { AccessToken, CreatedAccessToken } from "@/schemas/accessTokens";

export interface GetAccessTokensParams {
  status?: AccessToken["status"];
  page?: number;
  per_page?: number;
}

export type CreateAccessTokenPayload = Pick<
  AccessToken,
  "name" | "description" | "expires_at" | "abilities" | "rate_limits"
>;

export type UpdateAccessTokenPayload = CreateAccessTokenPayload;

class AccessTokensService {
  async list(params: GetAccessTokensParams = {}) {
    const response = await axiosClient.get<IPaginatedResponse<AccessToken>>(
      endpoints.ACCESS_TOKENS.ALL,
      { params }
    );

    return validatePaginatedResponseWithSchema(
      accessTokenSchema,
      response.data
    );
  }

  async create(data: CreateAccessTokenPayload) {
    const response = await axiosClient.post<{ data: CreatedAccessToken }>(
      endpoints.ACCESS_TOKENS.ALL,
      data
    );

    return validateWithSchema(createdAccessTokenSchema, response.data.data);
  }

  async update({
    tokenId,
    data,
  }: {
    tokenId: AccessToken["id"];
    data: UpdateAccessTokenPayload;
  }) {
    const response = await axiosClient.put<{ data: AccessToken }>(
      formatString(endpoints.ACCESS_TOKENS.SINGLE, tokenId),
      data
    );

    return validateWithSchema(accessTokenSchema, response.data.data);
  }

  async delete(tokenId: AccessToken["id"]) {
    await axiosClient.delete(
      formatString(endpoints.ACCESS_TOKENS.SINGLE, tokenId)
    );
  }
}

export default new AccessTokensService();
